import { useState, useRef } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';


const PortfolioGallery = ({ portfolioItems }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // مرجع لنقطة البداية عند اللمس
  const touchStart = useRef({ x: 0, y: 0 });

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setSelectedImage(portfolioItems?.[index]);
  };

  const closeLightbox = () => setSelectedImage(null);

  const navigateLightbox = (direction) => {
    const len = portfolioItems?.length || 1;
    const newIndex = direction === 'next'
      ? (lightboxIndex + 1) % len
      : (lightboxIndex - 1 + len) % len;
    setLightboxIndex(newIndex);
    setSelectedImage(portfolioItems?.[newIndex]);
  };

  const handleTouchStart = (e) => {
    touchStart.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  };

  const handleTouchEnd = (e) => {
    const dx = touchStart.current.x - e.changedTouches[0].clientX;
    const dy = touchStart.current.y - e.changedTouches[0].clientY;

    // سحب للأسفل بأكثر من 80px → إغلاق
    if (dy < -80 && Math.abs(dy) > Math.abs(dx)) {
      closeLightbox();
      return;
    }

    // سحب أفقي بأكثر من 50px → التنقل بين الصور
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      navigateLightbox(dx > 0 ? 'next' : 'prev');
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {portfolioItems?.map((item, index) => (
          <div
            key={item?.id}
            onClick={() => openLightbox(index)}
            className="group relative aspect-[4/3] overflow-hidden rounded-xl cursor-pointer shadow-warm hover:shadow-warm-lg transition-smooth"
          >
            <Image
              src={item?.image}
              alt={item?.imageAlt}
              className="w-full h-full object-cover group-hover:scale-110 transition-smooth"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-smooth">
              <div className="absolute bottom-0 right-0 left-0 p-4">
                <p className="hidden text-white/80 text-xs md:text-sm">{item?.category}</p>
              </div>
            </div>
            <div className="absolute top-3 left-3 opacity-0 group-hover:opacity-100 transition-smooth">
              <div className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center">
                <Icon name="ZoomIn" size={20} className="text-foreground" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/95 z-[2000] flex flex-col"
          style={{
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
          dir="rtl"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* خلفية قابلة للنقر للإغلاق */}
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute inset-0 z-0"
            aria-label="إغلاق"
          />

          {/* الصورة — تملأ المساحة */}
          <div
            className="flex-1 flex items-center justify-center relative z-[2005] px-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* سهم السابق */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); navigateLightbox('prev'); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 hover:bg-white/35 backdrop-blur-sm rounded-full flex items-center justify-center z-[2010] touch-manipulation"
              aria-label="السابق"
            >
              <Icon name="ChevronRight" size={22} className="text-white" />
            </button>

            <Image
              src={selectedImage?.image}
              alt={selectedImage?.imageAlt}
              className="max-w-full max-h-[75vh] object-contain rounded-lg select-none"
              draggable={false}
            />

            {/* سهم التالي */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); navigateLightbox('next'); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 hover:bg-white/35 backdrop-blur-sm rounded-full flex items-center justify-center z-[2010] touch-manipulation"
              aria-label="التالي"
            >
              <Icon name="ChevronLeft" size={22} className="text-white" />
            </button>
          </div>

          {/* شريط أسفل: عنوان + عداد + زر إغلاق */}
          <div
            className="relative z-[2010] flex items-center justify-between px-5 py-4 bg-black/40 backdrop-blur-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-1 min-w-0">
              <p className="text-white/50 text-xs mt-0.5">
                {lightboxIndex + 1} / {portfolioItems?.length}
              </p>
            </div>

            {/* زر الإغلاق — أسفل الشاشة، سهل الوصول بالإبهام */}
            <button
              type="button"
              onClick={closeLightbox}
              className="w-10 h-10 bg-white/15 hover:bg-white/25 rounded-full flex items-center justify-center touch-manipulation active:scale-95 transition-transform"
              aria-label="إغلاق"
            >
              <Icon name="X" size={20} className="text-white" />
            </button>
          </div>

          {/* تلميح السحب — يختفي بعد ثانيتين */}
          <p className="absolute top-4 left-1/2 -translate-x-1/2 text-white/40 text-xs z-[2010] pointer-events-none select-none">
            اسحب للتنقل · اسحب للأسفل للإغلاق
          </p>
        </div>
      )}
    </>
  );
};

export default PortfolioGallery;
